import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logementAPI } from '../../service/api';
import { FiPlus, FiEdit2, FiTrash2, FiMapPin, FiHome } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { confirmAction, showError, showSuccess, toast } from '../../service/swal';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function MesLogements() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const res = await logementAPI.getMyProperties();
      setProperties(res.data.properties || []);
    } catch (err) { console.error(err); }
    finally { if (!skipLoading) setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);
  useAutoRefresh(() => fetch(true), 30000);

  const handleDelete = async (id, titre) => {
    const confirmed = await confirmAction('Supprimer ce logement ?', `"${titre}" sera définitivement supprimé.`);
    if (!confirmed) return;
    try { await logementAPI.delete(id); fetch(); showSuccess('Logement supprimé'); }
    catch (err) { showError(err.response?.data?.error || 'Erreur'); }
  };

  const typeLabels = { studio: 'Studio', appartement: 'Appartement', maison: 'Maison', chambre: 'Chambre' };

  return (
    <div className="page-container py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Mes logements</h1>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>{properties.length} annonce(s) publiée(s)</p>
        </div>
        <Link to="/logements/nouveau" className="btn-primary-root flex items-center gap-2">
          <FiPlus /> Publier une annonce
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent' }} />
        </div>
      ) : properties.length === 0 ? (
        <div className="card-root p-12 text-center">
          <FiHome className="w-16 h-16 mx-auto mb-4" style={{ color: '#E5E5E0' }} />
          <p className="text-lg font-serif font-bold" style={{ color: '#0A192F' }}>Aucune annonce</p>
          <p className="text-sm mt-1 mb-6" style={{ color: '#7A828A' }}>Vous n'avez pas encore publié de logement</p>
          <Link to="/logements/nouveau" className="btn-primary-root">Publier votre première annonce</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {properties.map((p) => (
            <div key={p.id} className="card-root p-5 flex items-center gap-5">
              <div className="w-28 h-24 flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#E5E5E0' }}>
                {p.images && p.images.length > 0 ? (
                  <img src={resolveUploadUrl(p.images[0])} alt={p.titre} className="w-full h-full object-cover" />
                ) : (
                  <FiHome className="w-8 h-8" style={{ color: '#88929B' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 border-2"
                    style={{ color: '#0A192F', borderColor: '#0A192F' }}>{typeLabels[p.type] || p.type}</span>
                  <span className={`text-[11px] font-bold tracking-widest uppercase px-2 py-0.5 border-2 ${
                    p.statut === 'disponible' ? 'border-green-600 text-green-600' :
                    p.statut === 'reserve' ? 'border-yellow-600 text-yellow-600' : 'border-red-600 text-red-600'
                  }`}>{p.statut}</span>
                </div>
                <h3 className="font-serif font-bold text-base truncate" style={{ color: '#0A192F' }}>{p.titre}</h3>
                <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: '#7A828A' }}>
                  <FiMapPin className="w-3 h-3 flex-shrink-0" /> {p.adresse}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-lg" style={{ color: '#C59B6C' }}>{Number(p.prix).toLocaleString()} $</p>
                <p className="text-xs" style={{ color: '#7A828A' }}>{p.surface ? `${p.surface} m²` : ''}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/logements/${p.id}/editer`} className="btn-root text-xs px-3 py-2 flex items-center gap-1"
                  style={{ borderColor: '#0A192F', color: '#0A192F' }}>
                  <FiEdit2 className="w-3 h-3" /> Modifier
                </Link>
                <button className="btn-root text-xs px-3 py-2 flex items-center gap-1"
                  style={{ borderColor: '#C62828', color: '#C62828' }}
                  onClick={() => handleDelete(p.id, p.titre)}>
                  <FiTrash2 className="w-3 h-3" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
